import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  STATUS_LABEL,
  STATUS_LABEL_KEY,
  useCertificate,
  usePatchCertificate,
  useRevealCertificate,
  useRevokeCertificate,
  type CertificateStatus,
} from '@/hooks/use-certificates';
import { useTerminalProfiles } from '@/hooks/use-terminals';
import { useT } from '@/i18n/use-t';

function extractError(err: unknown): string | null {
  if (isAxiosError(err)) {
    const body = err.response?.data as { message?: string; code?: string } | undefined;
    if (body?.message) return body.code ? `${body.code}: ${body.message}` : body.message;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return null;
}

function statusVariant(s: CertificateStatus) {
  switch (s) {
    case 'active':
      return 'success' as const;
    case 'revoked':
      return 'destructive' as const;
    case 'expired':
    default:
      return 'outline' as const;
  }
}

function isoToDateInput(iso: string | null): string {
  if (iso === null) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function dateInputToIso(local: string): string | null {
  if (local === '') return null;
  const d = new Date(`${local}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function downloadBase64(filename: string, base64: string) {
  const binary = globalThis.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function CertificateDetailPage() {
  const t = useT('admin_ui');
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useCertificate(id);
  const { data: profiles } = useTerminalProfiles();
  const patch = usePatchCertificate();
  const revoke = useRevokeCertificate();
  const reveal = useRevealCertificate();

  const [label, setLabel] = useState('');
  const [terminalProfileId, setTerminalProfileId] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [notBefore, setNotBefore] = useState('');
  const [notAfter, setNotAfter] = useState('');
  const [banner, setBanner] = useState<string | null>(null);

  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');

  useEffect(() => {
    if (data === undefined) return;
    setLabel(data.label);
    setTerminalProfileId(data.terminalProfileId ?? '');
    setSerialNumber(data.serialNumber ?? '');
    setNotBefore(isoToDateInput(data.notBefore));
    setNotAfter(isoToDateInput(data.notAfter));
    setBanner(null);
    // Reset on identity OR updatedAt swap (revoke / patch flips updatedAt).
  }, [data?.id, data?.updatedAt]);

  if (isLoading)
    return <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>;
  if (data === undefined)
    return (
      <p className="text-sm text-muted-foreground">
        {t('certificates.detail.notFound', 'Certificate not found.')}
      </p>
    );

  const isLocked = data.status !== 'active';

  async function onSave() {
    if (data === undefined) return;
    setBanner(null);
    try {
      await patch.mutateAsync({
        id: data.id,
        body: {
          label: label.trim(),
          terminalProfileId: terminalProfileId === '' ? null : terminalProfileId,
          serialNumber: serialNumber.trim() === '' ? null : serialNumber.trim(),
          notBefore: dateInputToIso(notBefore),
          notAfter: dateInputToIso(notAfter),
        },
      });
    } catch (err) {
      setBanner(extractError(err) ?? t('certificates.detail.saveFailed', 'Save failed.'));
    }
  }

  async function onReveal() {
    if (data === undefined) return;
    setBanner(null);
    try {
      const out = await reveal.mutateAsync(data.id);
      const safeLabel = data.label.replace(/[^a-z0-9_-]+/gi, '_').slice(0, 64) || 'certificate';
      downloadBase64(`${safeLabel}.bin`, out.payloadBase64);
    } catch (err) {
      setBanner(extractError(err) ?? t('certificates.detail.revealFailed', 'Reveal failed.'));
    }
  }

  async function onConfirmRevoke() {
    if (data === undefined) return;
    setBanner(null);
    try {
      await revoke.mutateAsync({ id: data.id, reason: revokeReason.trim() });
      setRevokeOpen(false);
      setRevokeReason('');
    } catch (err) {
      setBanner(extractError(err) ?? t('certificates.detail.revokeFailed', 'Revoke failed.'));
    }
  }

  return (
    <div className="space-y-5">
      <header>
        <Link to="/certificates" className="text-xs text-muted-foreground hover:underline">
          {t('certificates.nav.back', '← Certificates')}
        </Link>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold">{data.label}</h1>
          <Badge variant={statusVariant(data.status)}>
            {t(STATUS_LABEL_KEY[data.status], STATUS_LABEL[data.status])}
          </Badge>
        </div>
        <p className="font-mono text-xs text-muted-foreground">
          {data.kind} · sha256 {data.payloadSha256.slice(0, 16)}…
        </p>
      </header>

      <section className="grid grid-cols-2 gap-4 rounded-md border border-border bg-card p-4">
        <div>
          <Label htmlFor="label">{t('certificates.field.label', 'Label')}</Label>
          <Input
            id="label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={isLocked}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="terminalProfileId">
            {t('certificates.detail.boundProfile', 'Bound terminal profile')}
          </Label>
          <select
            id="terminalProfileId"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={terminalProfileId}
            onChange={(e) => setTerminalProfileId(e.target.value)}
            disabled={isLocked}
          >
            <option value="">{t('certificates.detail.unbound', '— Unbound (library) —')}</option>
            {(profiles ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="serialNumber">
            {t('certificates.field.serialNumber', 'Serial number')}
          </Label>
          <Input
            id="serialNumber"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            disabled={isLocked}
            className="mt-1"
          />
        </div>
        <div /> {/* spacer for grid alignment */}
        <div>
          <Label htmlFor="notBefore">{t('certificates.field.notBefore', 'Not before')}</Label>
          <Input
            id="notBefore"
            type="date"
            value={notBefore}
            onChange={(e) => setNotBefore(e.target.value)}
            disabled={isLocked}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="notAfter">{t('certificates.field.notAfter', 'Not after')}</Label>
          <Input
            id="notAfter"
            type="date"
            value={notAfter}
            onChange={(e) => setNotAfter(e.target.value)}
            disabled={isLocked}
            className="mt-1"
          />
        </div>
        {banner !== null && (
          <p className="col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {banner}
          </p>
        )}
        <div className="col-span-2 flex flex-wrap gap-2">
          <Button onClick={onSave} disabled={isLocked || patch.isPending}>
            {patch.isPending
              ? t('common.saving', 'Saving…')
              : t('certificates.detail.saveMetadata', 'Save metadata')}
          </Button>
          <Button onClick={onReveal} disabled={isLocked || reveal.isPending} variant="outline">
            {reveal.isPending
              ? t('certificates.detail.revealing', 'Revealing…')
              : t('certificates.detail.downloadPayload', 'Download payload')}
          </Button>
          {!isLocked && (
            <Button
              onClick={() => setRevokeOpen(true)}
              variant="destructive"
              disabled={revoke.isPending}
            >
              {t('certificates.detail.revoke', 'Revoke')}
            </Button>
          )}
        </div>
        {isLocked && (
          <p className="col-span-2 text-xs text-muted-foreground">
            {data.status === 'revoked'
              ? `${t('certificates.detail.revokedPrefix', 'Revoked')} ${data.revokedAt?.slice(0, 10) ?? ''} — ${data.revokedReason ?? t('certificates.detail.noReason', '(no reason)')}.`
              : t('certificates.detail.expiredNote', 'Expired — upload a replacement to rotate.')}
          </p>
        )}
      </section>

      <section className="rounded-md border border-border bg-card p-4 text-xs">
        <header className="mb-2 text-sm font-medium">
          {t('certificates.detail.details', 'Details')}
        </header>
        <dl className="space-y-1">
          <Row label={t('certificates.detail.id', 'ID')} value={data.id} />
          <Row
            label={t('certificates.detail.payloadSha256', 'Payload sha256')}
            value={data.payloadSha256}
          />
          <Row label={t('certificates.detail.createdAt', 'Created at')} value={data.createdAt} />
          <Row label={t('certificates.detail.updatedAt', 'Updated at')} value={data.updatedAt} />
          <Row
            label={t('certificates.detail.revokedAt', 'Revoked at')}
            value={data.revokedAt ?? '—'}
          />
          <Row
            label={t('certificates.detail.revokeReason', 'Revoke reason')}
            value={data.revokedReason ?? '—'}
          />
        </dl>
      </section>

      <AlertDialog open={revokeOpen} onOpenChange={setRevokeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('certificates.detail.revokeTitle', 'Revoke certificate?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'certificates.detail.revokeDescription',
                'Revocation is irreversible. Bound terminals will lose this credential on the next sync. Provide a reason for the audit log.',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <Label htmlFor="revokeReason">{t('certificates.detail.reason', 'Reason')}</Label>
            <Input
              id="revokeReason"
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              placeholder={t('certificates.detail.reasonPlaceholder', 'e.g. compromised at vendor')}
              className="mt-1"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRevokeReason('')}>
              {t('common.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirmRevoke}
              disabled={revokeReason.trim().length === 0 || revoke.isPending}
            >
              {revoke.isPending
                ? t('certificates.detail.revoking', 'Revoking…')
                : t('certificates.detail.revoke', 'Revoke')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-40 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="flex-1 font-mono break-all">{value}</dd>
    </div>
  );
}
